import { flow } from '@1x2network/flow'
import { BasicLayout, Orientor } from '@1x2network/layout'
import { BezierCurve, createBezierTween, EmitterConfig } from '@1x2network/particle_trail'
import {
    applySpecification,
    Component,
    ContainerSpecification,
    createSprite,
    Spine,
    SpriteSpecification,
    ValueText,
} from '@1x2network/pixi_assets'
import { ValueTextSpecWithOrientation } from '@1x2network/pixi_dialogue'
import { SymbolFetcher } from '@1x2network/reel_set/lib/composite/components/fetcher/SymbolFetcher'
import gsap from 'gsap'
import { Container, Point, Sprite } from 'pixi.js'
import { CustomSymbol } from '../CustomSymbol'
import { SymbolModel } from '../model/SymbolModel'
import { ShapeOptions } from '../types/ShapeOptions'
import { isFeatureValueSymbol } from '../types/typeGuards'
import { formatPixiText } from '../utils/formatCurrency'
import { createParticleTrail } from '../utils/createParticleTrail'
import { Signal } from 'typed-signals'
import { ClickHandler } from './ClickHandler'
import { groupTimelineChildrenByTime } from '../utils/gsap'
import { tweenTint } from '../utils/spine'
import { TweenVars } from '@1x2network/tweens'
import { Pot } from '@1x2network/false_persistence'

/**
 * Popup that counts up to the total win amount at the end of a Hold and Win round.
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class HoldAndWinCountUp extends Component<HAWCountUpSpecification> {
    private totalWinSprite: Sprite
    private totalWinText: ValueText
    private fetcher: SymbolFetcher<CustomSymbol>
    private clickHandler: ClickHandler
    private pot: Pot

    public readonly onCollect = new Signal()

    constructor(options: {
        fetcher: SymbolFetcher<CustomSymbol>
        pot: Pot
        layout: BasicLayout
        clickHandler: ClickHandler
        parent?: Container
    }) {
        super({ spec: 'holdAndWinCountUp', parent: options.parent })
        this.pot = options.pot
        this.clickHandler = options.clickHandler
        this.fetcher = options.fetcher
        this.totalWinSprite = createSprite({ spec: this.specification.totalWinSprite, parent: this })
        this.totalWinText = new ValueText({
            spec: this.specification.totalWinText,
            parent: this,
            format: formatPixiText,
        })

        new Orientor({
            layout: options.layout,
            property: this.specification.totalWinText,
            onOrient: (active) => {
                applySpecification(this.totalWinText, active)
                this.totalWinText.maxWidth = active.maxWidth
                this.totalWinText.maxHeight = active.maxHeight
            },
        })
    }

    private collectSymbol(symbol: CustomSymbol, amount: number): GSAPAnimation {
        const startPosition = symbol.position.clone()
        const targetPosition = this.totalWinText.position.clone()

        const trail = createParticleTrail({
            startPosition: startPosition,
            endPosition: targetPosition,
            steps: this.specification.trail.steps,
            shape: this.specification.trail.shape,
            spec: {
                emitterConfigs: this.specification.emitterConfigs,
                particles: this.specification.particles,
            },
            parent: this,
        })

        return gsap
            .timeline()
            .add(
                trail.animate({
                    ...this.specification.trail.tweenVars,
                    onStart: async () => {
                        symbol.playCollect()
                    },
                    onComplete: () => {
                        this.onCollect.emit()
                    },
                    onParticlesComplete: () => {
                        trail.destroy()
                    },
                }),
            )
            .to(
                this.totalWinText.scale,
                {
                    x: 1.06,
                    y: 1.06,
                    duration: 0.1,
                    yoyo: true,
                    repeat: 1,
                    onStart: () => {
                        this.totalWinText.value += amount
                    },
                },
                '>-=0.05',
            )
    }

    private async animatePot(length: number) {
        const potSpine = this.pot.spine
        const potPosition = potSpine.position
        const zero = new Point(0, 0)
        const start = this.fetcher.getSymbolByPosition([0, 1]).position.subtract(potPosition)
        const end = this.fetcher.getSymbolByPosition([4, 1]).position.subtract(potPosition)

        const bottomLeft = this.fetcher.getSymbolByPosition([0, 2]).position.clone().subtract(potPosition)
        const bottomRight = this.fetcher.getSymbolByPosition([4, 2]).position.clone().subtract(potPosition)

        gsap.to(potSpine, { pixi: { scale: '+=0.3' }, duration: 0.5 })
        await gsap.to(this.pot, { ...start, ease: 'none', duration: 0.5 })
        await createBezierTween(new BezierCurve([start, bottomLeft, bottomRight, end], 10), {
            duration: length * 0.2,
            ease: 'power4.inOut',
            onUpdate: (point) => {
                this.pot.position = point
            },
        })
        await flow.wait(1000)
        gsap.to(potSpine, { pixi: { scale: '-=0.3' } })
        await gsap.to(this.pot, { ...zero, ease: 'none' })
    }

    private getCollectData(reels: SymbolModel[][]): Array<{ symbol: CustomSymbol; amount: number }> {
        const output: Array<{ symbol: CustomSymbol; amount: number }> = []
        for (let i = 0; i < reels.length; i++) {
            for (let j = 0; j < reels[i].length; j++) {
                const reelSymbol = reels[i][j]
                if (isFeatureValueSymbol(reelSymbol)) {
                    output.push({
                        symbol: this.fetcher.getSymbolByPosition([i, j]),
                        amount: reelSymbol.cash.end.currency,
                    })
                }
            }
        }
        return output
    }

    public async animate(reels: SymbolModel[][]): Promise<void> {
        const collectData = this.getCollectData(reels)
        const masterTimeline = gsap.timeline()
        const collectTimeline = gsap.timeline()
        // tween mask
        this.animatePot(collectData.length)

        this.totalWinText.value = 0
        masterTimeline.fromTo(this, { visible: false, alpha: 0 }, { visible: true, alpha: 1 })

        collectData.forEach((a, i) => {
            const { symbol, amount } = a
            const spine = symbol.spine as Spine
            collectTimeline
                .add(this.collectSymbol(symbol, amount), 0.3 + i * 0.1)
                .add(tweenTint(spine, 0x7f7f7f, { duration: 0.2 }), '<')
        })

        // Skip the animation by collecting all remaining symbols at once
        const connection = this.clickHandler.onClick.connect(() => {
            const { upcoming } = groupTimelineChildrenByTime(collectTimeline, collectTimeline.time())
            collectTimeline.add(upcoming, collectTimeline.time())
        })

        await masterTimeline.add(collectTimeline)
        await flow.wait(700)

        connection.disconnect()

        const spines = collectData.map((a) => a.symbol.spine as Spine)
        gsap.timeline()
            .add(tweenTint(spines, 0xffffff, { duration: 0.5 }), '+=0.5')
            .to(this, { alpha: 0, ease: 'none', visible: false }, '<')
    }
}

export interface HAWCountUpSpecification extends ContainerSpecification {
    totalWinSprite: SpriteSpecification
    totalWinText: ValueTextSpecWithOrientation
    emitterConfigs: EmitterConfig[]
    particles: string[][]
    trail: {
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
    }
}
